import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import ToolSeoHero from "../components/tools-seo/ToolSeoHero";
import ToolSeoBenefits from "../components/tools-seo/ToolSeoBenefits";
import ToolSeoFAQ from "../components/tools-seo/ToolSeoFAQ";
import ToolSeoCTA from "../components/tools-seo/ToolSeoCTA";
import SeoHead from "../components/seo/SeoHead";
import StructuredData, { buildFAQSchema } from "../components/seo/StructuredData";

const THEME_KEY = "hookora_theme";

const captionFaqs = [
  {
    question: "What is a caption generator?",
    answer:
      "A caption generator is a tool that helps create written caption options for social posts, videos, and content uploads. It is useful when you want faster writing support and clearer messaging."
  },
  {
    question: "Who is Hookora’s Caption Generator for?",
    answer:
      "It is built for creators, social media managers, brands, and anyone who wants a faster way to write stronger captions."
  },
  {
    question: "Can I use this for Instagram and TikTok captions?",
    answer:
      "Yes. Hookora is designed to support social content workflows, including captions that can be adapted for different platforms."
  },
  {
    question: "Why do captions matter?",
    answer:
      "Captions help frame your message, add context, and strengthen the overall impact of your content. A better caption can make your post feel more complete and easier to connect with."
  },
  {
    question: "Does Hookora only help with captions?",
    answer:
      "No. Hookora also includes tools for hooks, YouTube titles, hashtags, descriptions, short scripts, and thumbnail text."
  },
  {
    question: "Is this useful for short-form content creators?",
    answer:
      "Yes. Short-form creators often need faster ways to package content well, and stronger captions are part of that workflow."
  }
];

function CaptionGeneratorPage() {
  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [theme, setTheme] = useState(getStoredTheme());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const pageStyle = useMemo(
    () => ({
      background: isDark ? "#020617" : "#f8fafc",
      backgroundImage: isDark
        ? "radial-gradient(circle at top left, rgba(59,130,246,0.12), transparent 28%), radial-gradient(circle at top right, rgba(124,58,237,0.10), transparent 24%)"
        : "none",
      minHeight: "100vh"
    }),
    [isDark]
  );

  return (
    <div style={pageStyle}>
      <SeoHead
        title="Caption Generator | Hookora"
        description="Use Hookora’s Caption Generator to write clearer, more engaging captions for social content with better structure, messaging, and workflow speed."
        path="/caption-generator"
      />
      <StructuredData
        id="caption-generator-faq-schema"
        data={buildFAQSchema(captionFaqs)}
      />

      <Navbar />
      <ToolSeoHero
        badge="Hookora Tool • Caption Generator"
        title="Caption Generator for creators who want stronger social captions without overthinking"
        description="The Hookora Caption Generator helps creators write clearer, more engaging captions for social content. Instead of struggling to phrase your post, you get caption directions that feel more natural, more intentional, and easier to publish."
        highlights={[
          "Write faster",
          "Match your message",
          "Improve consistency",
          "Built for creator workflow"
        ]}
        primaryCtaLabel="Start Free"
        primaryCtaTo="/register"
        secondaryCtaLabel="See Features"
        secondaryCtaTo="/features"
        previewTitle="What the Caption Generator helps you do"
        previewItems={[
          {
            label: "Speed",
            text: "Go from a rough idea to a usable caption faster without getting stuck on wording."
          },
          {
            label: "Clarity",
            text: "Turn your content angle into captions that feel easier to understand and more polished."
          },
          {
            label: "Consistency",
            text: "Keep your posting workflow smoother when you need repeatable caption support."
          }
        ]}
      />

      <ToolSeoBenefits
        eyebrow="Why creators use it"
        title="A caption generator should help your content feel more natural, not more generic"
        description="Strong captions help your audience understand your message faster and connect with your content more easily. Hookora helps you build captions that feel sharper, more useful, and more aligned with your content style."
        benefits={[
          {
            title: "Write captions faster from one idea",
            text: "You do not need to spend too much time trying different wording every time you post. Hookora helps you move faster."
          },
          {
            title: "Improve clarity without making captions stiff",
            text: "A good caption feels easy to read and easy to understand. Hookora helps you get there without forcing the tone."
          },
          {
            title: "Support multiple social platforms",
            text: "Whether you are posting for Instagram, TikTok, LinkedIn, or other channels, stronger captions make your content easier to position."
          },
          {
            title: "Keep your workflow more consistent",
            text: "When you create often, caption writing can become repetitive. Hookora helps reduce that friction and keep your process moving."
          },
          {
            title: "Match captions with your wider content packaging",
            text: "Your captions work better when they connect with your hooks, scripts, and content angles. Hookora supports that fuller workflow."
          },
          {
            title: "Useful for beginners and active creators",
            text: "Whether you post occasionally or create constantly, a better caption workflow helps you publish with more confidence."
          }
        ]}
      />

      <ToolSeoFAQ
        title="Caption Generator FAQ"
        intro="Here are a few common questions about Hookora’s Caption Generator."
        faqs={captionFaqs}
      />

      <ToolSeoCTA
        badge="Start free with Hookora"
        title="Use Hookora’s Caption Generator to write better captions with less friction"
        description="If you want faster caption writing, clearer messaging, and a smoother content workflow, Hookora gives you a practical place to start."
        primaryLabel="Create Free Account"
        primaryTo="/register"
        secondaryLabel="Explore Features"
        secondaryTo="/features"
      />
    </div>
  );
}

export default CaptionGeneratorPage;